import React, { useEffect, useState, useCallback } from "react";
import regSchema from "../schemas/regulation.json";
import DynamicForm from "../components/DynamicForm/DynamicForm";
import DataTable from "../components/DataTable/DataTable";
import { apiGet, apiPost, apiPut } from "../services/api";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";

const autoSx = {
  "& .MuiOutlinedInput-root": { padding: "4px 32px 4px 14px !important", fontSize: 12 },
  "& .MuiOutlinedInput-root .MuiAutocomplete-input": { padding: "0 !important", fontSize: 12 },
  "& .MuiAutocomplete-popupIndicator": { padding: "1px", marginRight: "-2px" },
  "& .MuiAutocomplete-clearIndicator": { padding: "1px" },
};
import { Routes, Route, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import * as permissions from "./app_props";

export default function RegulationsPage() {
  const user = JSON.parse(sessionStorage.getItem("userInfo"));
  const [regs, setRegs] = useState([]);
  const SESSION_PAGE_SIZE_KEY = "compliance_ui_page_size";
  const defaultSize = (() => {
    try {
      const s = sessionStorage.getItem(SESSION_PAGE_SIZE_KEY);
      const n = Number(s);
      if (n) return n;
    } catch (e) {
      // ignore
    }
    return 10;
  })();
  const [regsPage, setRegsPage] = useState({
    page: 0,
    size: defaultSize,
    total: 0,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [compliances, setCompliances] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedCompliance, setSelectedCompliance] = useState(null);
  const [filters, setFilters] = useState({});

  const buildFilterQuery = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.set(`filter.${key}`, value);
      }
    });
    return params.toString();
  }, [filters]);

  const load = useCallback(async () => {
    try {
      const [cdata, tdata] = await Promise.all([
        apiGet("/api/compliances"),
        apiGet("/api/compliance-types"),
      ]);
      setCompliances(Array.isArray(cdata) ? cdata : cdata.content || []);
      setTypes(Array.isArray(tdata) ? tdata : tdata.content || []);
      // load locations and facilities to resolve ids from names (we need id for country/state and site)
      let locations = [];
      let facilities = [];
      // load regulations (first page)
      try {
        const query = buildFilterQuery();
        const r = await apiGet(
          `/api/regulations?page=0&size=${regsPage.size}${query ? `&${query}` : ""}`,
        );
        const content = Array.isArray(r) ? r : r.content || [];
        const mapped = content.map((it) => {
          const firstFac =
            Array.isArray(it.complianceFacility) && it.complianceFacility.length
              ? it.complianceFacility[0]
              : null;
          return {
            ...it,
            regulationName: it.name || it.title || it.regulationName,
            citation: it.citation,
            ehsCategory: it.ehsCategory,
            media: it.media,
            jurisdiction: it.jurisdiction,
            location: it.location,
            justification: firstFac ? firstFac.justification : null,
            sourceNo: it.sourceNo,
          };
        });
        setRegs(mapped);
        if (r && typeof r.totalElements !== "undefined") {
          setRegsPage((p) => ({ ...p, total: r.totalElements }));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load regulations", err);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to load master data", err);
    }
  }, []);

  // Fetch regulations with explicit page/size (used when page size changes)
  const fetchRegulations = useCallback(async () => {
    try {
      const query = buildFilterQuery();
      const r = await apiGet(
        `/api/regulations?page=${regsPage.page}&size=${regsPage.size}${query ? `&${query}` : ""}`,
      );
      const content = Array.isArray(r) ? r : r.content || [];
      const mapped = content.map((it) => {
        const firstFac =
          Array.isArray(it.complianceFacility) && it.complianceFacility.length
            ? it.complianceFacility[0]
            : null;
        return {
          ...it,
          regulationName: it.name || it.title || it.regulationName,
          citation: it.citation,
          ehsCategory: it.ehsCategory,
          media: it.media,
          jurisdiction: it.jurisdiction,
          location: it.location,
          justification: firstFac ? firstFac.justification : null,
          sourceNo: it.sourceNo,
        };
      });
      setRegs(mapped);
      if (r && typeof r.totalElements !== "undefined") {
        setRegsPage((p) => ({
          ...p,
          total: r.totalElements,
          page: regsPage.page,
          size: regsPage.size,
        }));
      } else {
        setRegsPage((p) => ({
          ...p,
          page: regsPage.page,
          size: regsPage.size,
        }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to load regulations", err);
    }
  }, [buildFilterQuery, regsPage.size, regsPage.page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    fetchRegulations();
  }, [fetchRegulations, regsPage.size, regsPage.page]);

  // handler passed to DataTable to react to page-size changes
  const handlePageSizeChange = useCallback((newSize) => {
    try {
      sessionStorage.setItem(SESSION_PAGE_SIZE_KEY, String(newSize));
    } catch (e) {
      // ignore
    }
    // re-fetch regulations with new size starting at page 0
    setRegsPage((p) => ({ ...p, page: 0, size: newSize }));
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setRegsPage((p) => ({ ...p, page: newPage }));
  }, []);

  async function handleSave(formValues) {
    try {
      const general = formValues.general || {};
      const analysis = formValues.analysis || {};
      const admin = formValues.admin || {};
      // find compliance type id for regulation
      const regType =
        types.find((t) => (t.type || "").toLowerCase() === "regulation") ||
        types[0];
      const payload = {
        title: general.regulationName || general.name,
        agency: general.agency,
        name: general.regulationName || general.name,
        jurisdiction: general.jurisdiction,
        citation: general.citation,
        ruleNo: general.ruleNo,
        regulationLink: general.regulationLink,
        media: general.media,
        location: general.location,
        ehsCategory: general.ehsCategory,
        subTopic: general.theme,
        screeningApplicability: analysis.screeningApplicability,
        regulationRevisionDate: analysis.revisionDate || null,
        recordStatus: admin.recordStatus,
        recordNumber: admin.recordNo,
        enabled: true,
        country: admin.country,
        state: admin.stateProvince,
        preserveRequirementTasks: admin.preserveRequirementTasks === true,
        managingRegion: admin.managingRegion,
        area: admin.area,
        compliance: selectedCompliance ? { id: selectedCompliance.id } : null,
        complianceType: regType ? { id: regType.id } : null,
      };

      // include modifiedAt when editing so backend can validate concurrency
      if (admin && admin.dateModified) {
        payload.modifiedAt = admin.dateModified;
      }

      // map location determinations to complianceFacility for backend
      const locApp = formValues.locationApplicability || {};
      if (locApp.applicabilityLevel) {
        payload.applicabilityLevel = locApp.applicabilityLevel;
      }
      if (Array.isArray(locApp.locationDeterminations)) {
        payload.complianceFacility = locApp.locationDeterminations.map((d) => ({
          // include existing complianceFacility id when editing so backend can merge-by-id
          id: d.id || d._id || null,
          // send location id when resolvable, otherwise send the location name so backend can resolve by name
          location: d.location,
          applicable: !!d.applicable,
          justification: d.justification || null,
          recordNo: d.recordNo || null,
        }));
      }
      // If editing an existing regulation (formValues._id) call update, otherwise create
      const existingId = formValues._id || formValues.id || null;
      await (existingId
        ? await apiPut(`/api/crs/${existingId}`, payload)
        : await apiPost("/api/crs", payload));
      alert("Regulation saved");
      load();
      navigate("/regulations");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      alert("Save failed: " + err.message);
    }
  }

  return (
    <div className="h-[89.8vh]">
      <Routes>
        <Route
          index
          element={
            <Box sx={{ m: 1 }}>
              <Card>
                <CardContent>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6">Regulations</Typography>
                    <div>
                      {user &&
                        [permissions.COMPLIANCE_CAN_ADD_REGULATIONS].some(
                          (authority) =>
                            user.authorities === undefined
                              ? false
                              : user.authorities.includes(authority),
                        ) && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate("/regulations/new")}
                          >
                            Add New Regulation
                          </Button>
                        )}
                    </div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <DataTable
                      schema={regSchema}
                      rows={regs}
                      onFiltersChange={setFilters}
                      extraControls={null}
                      onPageSizeChange={handlePageSizeChange}
                      totalCount={regsPage.total}
                      currentPage={regsPage.page}
                      onPageChange={handlePageChange}
                      onEdit={(idx, row) => {
                        // Map flat row into the nested shape expected by DynamicForm
                        const mapCountry = (c) => (c && (c.name || c)) || null;
                        const defaultValues = {
                          _id: row._id || row.id || null,
                          general: {
                            regulationName: row.regulationName || row.name,
                            citation: row.citation,
                            jurisdiction: row.jurisdiction,
                            media: row.media,
                            location: row.location,
                            agency: row.agency,
                            ruleNo: row.ruleNo,
                            regulationLink: row.regulationLink,
                            ehsCategory: row.ehsCategory,
                            theme: row.theme || row.subTopic,
                            summary: row.summary,
                          },
                          analysis: {
                            screeningApplicability:
                              row.screeningApplicability ||
                              row.analysis?.screeningApplicability,
                            revisionDate:
                              row.regulationRevisionDate ||
                              row.revisionDate ||
                              row.analysis?.revisionDate,
                          },
                          admin: {
                            recordNo:
                              row.recordNo || row.recordNumber || row.sourceNo,
                            recordStatus: row.recordStatus,
                            country: row.country,
                            stateProvince: row.state,
                            managingRegion: row.managingRegion,
                            area: row.area,
                            createdBy: row.createdBy,
                            dateCreated: row.createdAt,
                            modifiedBy: row.modifiedBy,
                            dateModified: row.modifiedAt,
                          },
                          locationApplicability: {
                            applicabilityLevel: row.applicabilityLevel,
                            locationDeterminations: Array.isArray(
                              row.complianceFacility,
                            )
                              ? row.complianceFacility.map((cf) => ({
                                  id: cf.id || cf._id || null,
                                  location: cf.location,
                                  applicable: !!cf.applicable,
                                  justification: cf.justification || null,
                                  recordNo: cf.recordNo || null,
                                }))
                              : row.locationDeterminations || [],
                          },
                          // requirements: Array.isArray(row.requirements)
                          //   ? row.requirements.map((r) => ({
                          //       summary: r.summary || r.description || r.name,
                          //       appliesTo: r.appliesTo || r.applies || "",
                          //       roaRefId: r.roaRefId || r.roaRef || "",
                          //       citation: r.citation || "",
                          //       record: r.recordNo || r.record || "",
                          //     }))
                          //   : [],
                          requirements: row.requirements
                            ? row.requirements
                            : [],
                        };
                        // if the row has a compliance reference, set it in location state
                        const selComp = row.compliance || null;
                        navigate(`/regulations/new`, {
                          state: { defaultValues },
                        });
                        // also set selectedCompliance so the Autocomplete shows the value
                        if (selComp) {
                          const match = compliances.find(
                            (c) => c.id === selComp.id,
                          );
                          setSelectedCompliance(match || selComp);
                        }
                      }}
                      onDelete={(idx) => {
                        // deleting from local store not implemented here
                      }}
                    />
                  </div>
                </CardContent>
              </Card>
            </Box>
          }
        />
        <Route
          path="new"
          element={
            <Box sx={{ m: 1, maxHeight: "87vh", overflowY: "auto" }}>
              <Card>
                <CardContent>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6">New Regulation</Typography>
                    <div />
                  </div>
                  <div
                    style={{ marginTop: 12 }}
                    className="flex justify-center"
                  >
                    <div style={{ width: "100%", maxWidth: 900 }}>
                      <div style={{ marginBottom: 8 }}>
                        <div className="grid grid-cols-[160px_1fr] items-center gap-2 py-px">
                          <span className="text-xs text-gray-600 whitespace-nowrap">Compliance</span>
                          <Autocomplete
                            sx={autoSx}
                            options={compliances}
                            getOptionLabel={(o) => (o ? o.name : "")}
                            value={selectedCompliance}
                            onChange={(_, v) => setSelectedCompliance(v)}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            fullWidth
                            clearOnEscape
                            renderInput={(params) => (
                              <TextField {...params} size="small" margin="dense" />
                            )}
                            ListboxProps={{ style: { maxHeight: 240 } }}
                          />
                        </div>
                      </div>
                      <DynamicForm
                        schema={regSchema}
                        defaultValues={location.state?.defaultValues || {}}
                        onSubmit={handleSave}
                        onCancel={() => navigate("/regulations")}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Box>
          }
        />
      </Routes>
    </div>
  );
}
